import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { request } from "hooks/useOkoApi/useOkoApi";
import { useAuthReady } from "hooks/useAuthReady";

const EMPTY_ROWS = [];
const NO_ERRORS = {};
const NO_OP = () => {};
const CHALLENGE_FETCH_FAILED = "Could not load this session's questions.";

// The console renders exactly one session, so it is index 0 of the list the
// teacher's components page through.
export const CONSOLE_SESSION_INDEX = 0;

/**
 * The session's challenge rows, in the shape the teacher's components expect.
 *
 * This is all the console has to fetch for itself: the figures behind the report
 * are resolved by `useReportDeterministic`, the one builder every surface
 * shares. The rows come from the same `account/session_history_challenges`
 * endpoint the teacher's Session Reports page uses, scoped to the educator who
 * ran the session because the console's own user is an admin who did not.
 *
 * The query state is returned alongside them so `Summary`'s spinner, its error
 * and its Try Again all behave as the teacher's rather than being faked.
 *
 * @param {string|null} sessionId The session under test.
 * @param {string|null} educatorId The educator who ran it, from the prefill.
 * @returns {{challengeData: Object<string, Array>, challengeDataErrors:
 *   Object<string, string>, isChallengeDataLoading: Object<number, boolean>,
 *   fetchChallengeData: Function, setChallengeDataErrors: Function}}
 */
export const useConsoleChallengeRows = (sessionId, educatorId) => {
  const authReady = useAuthReady();

  const { data, error, isFetching, refetch } = useQuery({
    queryKey: ["console-session-challenges", sessionId, educatorId],
    queryFn: () =>
      request(
        `account/session_history_challenges/${sessionId}` +
          (educatorId ? `?educator_id=${educatorId}` : ""),
        { method: "GET" }
      ),
    enabled: Boolean(sessionId) && authReady,
    staleTime: Infinity,
    cacheTime: Infinity,
  });

  // A failed or still-pending read gives something that is not a list, and a
  // report that renders without these rows is better than one that throws.
  const rows = useMemo(() => (Array.isArray(data) ? data : EMPTY_ROWS), [data]);

  return useMemo(
    () => ({
      // Keyed by session: the shape both Transcript and SummaryDeprecated index
      // into. Handing them an array (or nothing) is what made every
      // `challenge_response:` line degrade to raw text.
      challengeData: { [sessionId]: rows },
      challengeDataErrors: error
        ? { [sessionId]: error?.detail || error?.message || CHALLENGE_FETCH_FAILED }
        : NO_ERRORS,
      isChallengeDataLoading: { [CONSOLE_SESSION_INDEX]: isFetching },
      fetchChallengeData: refetch,
      // react-query owns the error, so clearing it is the refetch's job. Try
      // Again calls this first and refetches immediately after.
      setChallengeDataErrors: NO_OP,
    }),
    [sessionId, rows, error, isFetching, refetch]
  );
};
